import React, { useEffect, useMemo, useState } from "react";
import config from '@/config';
import { useDispatch, useSelector } from "react-redux";
import { addToast } from "../../store/app/action.js";
import Badge from "../../components/Badges.jsx";
import SubscriptionDot from "./subscriptionDot.jsx";
import { useHotkeys } from "react-hotkeys-hook";

export function isMerchantOffboarded(merchant) {
  if (!merchant) return false;
  if (merchant.kill_switch) return true;
  const locations = merchant.locations || [];
  return locations.length > 0 && locations.every((location) => location.kill_switch);
}

function MerchantTableItem({
  merchant,
  handleClick,
  subscriptions,
  setShowArchive,
}) {
  const { selectedMerchant } = useSelector((state) => state.app);
  const [showContext, setShowContext] = useState(false);
  const dispatch = useDispatch();
  const contextRef = React.useRef();
  const subscription = useMemo(() => {
    return subscriptions.find(
      (sub) => sub.id === merchant.stripe_subscription_id
    );
  }, [merchant, subscriptions]);
  const plan = useMemo(() => {
    return (subscription?.items?.data || []).find(
      (item) => item.price.product.metadata.type === "direct_base_subscription"
    );
  }, [subscription]);
  const addons = useMemo(() => {
    return (subscription?.items?.data || []).filter(
      (item) => item.price?.product?.metadata?.type === "addon"
    );
  }, [subscription]);
  const pauseCollection = useMemo(() => {
    const raw = merchant?.pause_collection ?? subscription?.pause_collection;
    if (!raw) return null;
    const behavior =
      (typeof raw === "object" && raw.behavior) ||
      (typeof subscription?.pause_collection === "object" &&
        subscription.pause_collection.behavior) ||
      "";
    return { behavior };
  }, [merchant, subscription]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (contextRef.current && !contextRef.current.contains(event.target)) {
        setShowContext(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const [isShiftDown, setIsShiftDown] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Shift") setIsShiftDown(true);
    };
    const handleKeyUp = (e) => {
      if (e.key === "Shift") setIsShiftDown(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  useHotkeys(
    "shift+ctrl+c",
    () => {
      if (!showContext) return;
      copyID();
      setShowContext(false)
    },
    { preventDefault: true }
  );
  useHotkeys(
    "ctrl+c",
    () => {
      if (!showContext) return;
      copyEasyID();
      setShowContext(false)
    },
    { preventDefault: true }
  );
  useHotkeys(
    "ctrl+a",
    () => {
      if (!showContext) return;
      archive();
      setShowContext(false)
    },
    { preventDefault: true }
  );
  useHotkeys(
    "ctrl+e",
    () => {
      if (!showContext) return;
      edit();
      setShowContext(false)
    },
    { preventDefault: true }
  );
  useHotkeys(
    "ctrl+g",
    () => {
      if (!showContext) return;
      goToMothership();
      setShowContext(false)
    },
    { preventDefault: true }
  );



  const copyID = () => {
    navigator.clipboard
      .writeText(`ObjectId('${merchant.id}')`)
      .then(() => {
        dispatch(
          addToast({
            type: "success",
            message: "Copied ID to clipboard",
            timer: 5,
          })
        );
      })
      .catch((err) => {
        dispatch(
          addToast({
            type: "error",
            message: "Failed to copy ID to clipboard",
            timer: 5,
          })
        );
      });
  };
  const goToMothership = () => {
    window.open(
      merchant.custom_direct_web_urls?.[0]
        ? `https://${merchant.custom_direct_web_urls?.[0]}`
        : `${config.direct_web_prefix}${
            merchant.direct_web_url ?? merchant.store_id
          }`,
      "_blank"
    );
  };
  const archive = () => {
    setShowArchive(merchant);
  };
  const edit = () => {
    handleClick(merchant.id);
  };

  const copyEasyID = () => {
    navigator.clipboard
      .writeText(`${merchant.name} (${merchant.easy_id})`)
      .then(() => {
        dispatch(
          addToast({
            type: "success",
            message: "Copied EasyID + Name to clipboard",
            timer: 5,
          })
        );
      })
      .catch((err) => {
        dispatch(
          addToast({
            type: "error",
            message: "Failed to copy EasyID + Name to clipboard",
            timer: 5,
          })
        );
      });
  };

  return (
    <tr>
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="flex flex-col items-start">
          <div className="font-medium text-slate-800 dark:text-slate-100">
            {merchant.easy_id}{" "}
          </div>
          {merchant.easy_id === selectedMerchant?.easy_id && (
            <Badge color="green">Current</Badge>
          )}
        </div>
      </td>
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-left">{merchant.name}</div>
        <div className="text-left">{merchant.email}</div>
      </td>
      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <div className="text-left">{`${
          merchant.locations.filter((l) => l.enabled).length
        } / ${merchant.locations.length}`}</div>
      </td>
      <td>
        {!merchant.manual_billing && (
          <>
            <span className={"capitalize"}>
              <SubscriptionDot
                subscription={subscription}
                collectionPaused={Boolean(pauseCollection)}
              />{" "}
              {plan?.price?.nickname?.replace(" Subscription", "")}
              {isMerchantOffboarded(merchant) && (
                <span className="normal-case"> (Off boarded)</span>
              )}
              <br />
              <span className={"capitalize"}>{subscription?.status}</span> (
              {plan?.quantity || 0} licenses)
              {pauseCollection && (
                <>
                  <br />
                  <span className="normal-case">
                    Collection paused ({pauseCollection.behavior})
                  </span>
                </>
              )}
              {subscription?.status === "trialing" && (
                <>
                  <br />
                  Ends:{" "}
                  {new Date(subscription.trial_end * 1000).toLocaleDateString()}
                </>
              )}
              {addons?.length > 0 && (
                <>
                  <br />
                  {addons.map((addon) => {
                    return (
                      <div key={`addon_${merchant.easy_id}_${addon.id}`}>
                        Addon: {addon.plan.metadata.addon_name}
                      </div>
                    );
                  })}
                </>
              )}
            </span>
          </>
        )}
        {merchant.manual_billing && (
          <>
            Manual Billing
            {isMerchantOffboarded(merchant) && (
              <span className="normal-case"> (Off boarded)</span>
            )}
          </>
        )}
      </td>

      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setShowContext(!showContext);
          }}
          className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400 rounded-full"
        >
          <span className="sr-only">Menu</span>
          <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="2" />
            <circle cx="10" cy="16" r="2" />
            <circle cx="22" cy="16" r="2" />
          </svg>
        </button>
        <div
          ref={contextRef}
          className={`absolute right-0 mt-2 w-96 py-2 z-10 bg-white dark:bg-theme-surface-accent rounded-sm shadow-lg border border-slate-200 dark:border-slate-700 ${
            showContext ? "block" : "hidden"
          }`}
        >
          {[
            {
              name: "Edit",
              onClick: edit,
              shortcut: ["ctrl", "e"],
            },
            {
              name: "Copy EasyID & Name to clipboard",
              onClick: copyEasyID,
              shortcut: ["ctrl", "c"],
            },
            isShiftDown && {
              name: "Copy ID to clipboard",
              onClick: copyID,
              shortcut: ["shift", "ctrl", "c"],
            },
            {
              name: "Go to mothership",
              disabled: merchant.locations.length <= 1,
              onClick: goToMothership,
              shortcut: ["ctrl", "g"],
            },
            {
              name: "Archive",
              danger: true,
              onClick: archive,
              shortcut: ["ctrl", "a"],
            },
          ]
            .filter((a) => a)
            .map((a) => (
              <span
                key={a.name}
                onClick={(e) => {
                  if (a.disabled) return;
                  e.stopPropagation();
                  e.preventDefault();
                  a.onClick();
                  setShowContext(false);
                }}
                className={`flex items-center justify-between px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-900 ${
                  a.disabled
                    ? "text-slate-400 dark:text-slate-600 cursor-not-allowed"
                    : a.danger
                    ? "text-red-500 dark:text-red-500"
                    : "text-slate-800 dark:text-slate-100"
                }`}
              >
                {a.name}
                {a.shortcut && (
                  <span className="flex gap-1">
                    {a.shortcut.map((s, idx) => (
                      <kbd
                        key={idx}
                        className="inline-block px-1.5 py-0.5 rounded border text-xs font-mono text-slate-500 border-slate-300 dark:text-slate-400 dark:border-slate-600"
                      >
                        {s[0].toUpperCase() + s.slice(1)}
                      </kbd>
                    ))}
                  </span>
                )}
              </span>
            ))}
        </div>
        {/*<div className="text-left">*/}
        {/*  {merchant.locations.length > 1 ? (*/}
        {/*    <PrimaryButton*/}
        {/*      onClick={(e) => {*/}
        {/*        e.stopPropagation();*/}
        {/*        window.open(*/}
        {/*          merchant.custom_direct_web_urls?.[0]*/}
        {/*            ? `https://${merchant.custom_direct_web_urls?.[0]}`*/}
        {/*            : `${config.direct_web_prefix}${*/}
        {/*                merchant.direct_web_url ?? merchant.store_id*/}
        {/*              }`,*/}
        {/*          "_blank"*/}
        {/*        );*/}
        {/*      }}*/}
        {/*    >*/}
        {/*      Open*/}
        {/*    </PrimaryButton>*/}
        {/*  ) : (*/}
        {/*    "-"*/}
        {/*  )}*/}
        {/*</div>*/}
      </td>
    </tr>
  );
}

export default MerchantTableItem;
